// src/components/union-orders/UnionOrderItem.tsx

import { useState, useMemo, useEffect } from 'react'
import { Box, HStack, VStack, Text, IconButton, Separator } from '@chakra-ui/react'
import {
  LuCalendarRange,
  LuChevronDown,
  LuChevronUp,
  LuPaperclip,
  LuInfo,
  LuDownload,
  LuFileText,
} from 'react-icons/lu'

import type { OrderAttachmentDto } from '@/api/dto/union.order.dto'

import { toast } from '@/components/ui/toaster/toasterHelpers'
import CorrectionSection from './CorrectionSection'
import RejectionSection from './RejectionSection'
import type { CapacityInfo } from './CheckCapacityPopup'
import { listOrderAttachments } from '@/api/order-attachments.api'
import { calculateOrderCommission, type Order } from '@/common/order.common'
import { API_BASE_URL } from '@/config'
import { hoursToUnits, mapApiStatusToUiStatus } from '@/helper/order.helper'
import { OrderStatusPill } from '@/components/order/OrderStatusPill'
import { ServiceStatBoxes, type ServiceValues } from './ServiceStatBoxes'
import { OrderHistoryTimeline } from './OrderHistoryTimeline'

/* ------------------------------------------------------------
 * MAIN COMPONENT
 * ------------------------------------------------------------ */

export type OrderActionMode = 'default' | 'correction' | 'reject'

export interface UnionOrderItemProps {
  order: Order
  bankName?: string
  isExpanded: boolean
  onToggleExpand: () => void

  activeTab: string
  onStatusChange: (newStatus: string) => void

  capacityInfo: CapacityInfo
  onRefresh: () => void
  remainingCurrentYearUnits: CapacityInfo

  /** Controlled correction/reject mode (falls back to internal state). */
  mode?: OrderActionMode
  onModeChange?: (mode: OrderActionMode) => void
}

function formatEUR(value?: number) {
  if (value == null) return '—'
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

export default function UnionOrderItem({
  order,
  bankName,
  isExpanded,
  onToggleExpand,
  onStatusChange,
  capacityInfo,
  onRefresh,
  remainingCurrentYearUnits,
  mode: controlledMode,
  onModeChange,
}: UnionOrderItemProps) {
  const [internalMode, setInternalMode] = useState<OrderActionMode>('default')
  const mode = controlledMode ?? internalMode
  const setMode = (m: OrderActionMode) => (onModeChange ? onModeChange(m) : setInternalMode(m))

  const { buckets, attachmentCount } = order

  const [attachments, setAttachments] = useState<OrderAttachmentDto[]>([])
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)

  /* ------------------------------------------------------------
   * Extract bucket values
   * ------------------------------------------------------------ */
  type UiServiceType = 'TOJ' | 'CONSULTING' | 'TRAINING' | 'PILOT'

  const quantities = useMemo<Record<UiServiceType, number>>(() => {
    const result: Record<UiServiceType, number> = { TOJ: 0, CONSULTING: 0, TRAINING: 0, PILOT: 0 }

    buckets.forEach(b => {
      if (b.serviceType in result) {
        result[b.serviceType as UiServiceType] = b.plannedHours
      }
    })

    return result
  }, [buckets])

  const quantityUnits: ServiceValues = useMemo(
    () => ({
      TOJ: hoursToUnits('TOJ', quantities.TOJ),
      CONSULTING: hoursToUnits('CONSULTING', quantities.CONSULTING),
      TRAINING: hoursToUnits('TRAINING', quantities.TRAINING),
      PILOT: hoursToUnits('PILOT', quantities.PILOT),
    }),
    [quantities]
  )

  /* ------------------------------------------------------------
   * Warning if exceeding capacity
   * ------------------------------------------------------------ */
  const exceeds = {
    TOJ: quantityUnits.TOJ! > remainingCurrentYearUnits.TOJ,
    CONSULTING: quantityUnits.CONSULTING! > remainingCurrentYearUnits.CONSULTING,
    TRAINING: quantityUnits.TRAINING! > remainingCurrentYearUnits.TRAINING,
    PILOT: quantityUnits.PILOT! > (remainingCurrentYearUnits.PILOT ?? 0),
  }

  const showWarning = exceeds.TOJ || exceeds.CONSULTING || exceeds.TRAINING || exceeds.PILOT

  const sibName = order.username ?? order.userName ?? 'SIB'

  /* ------------------------------------------------------------
   * Load attachments when expanded
   * ------------------------------------------------------------ */
  useEffect(() => {
    if (!isExpanded) return
    if (!Number.isFinite(attachmentCount) || attachmentCount <= 0) return

    let cancelled = false
    const fetchAttachments = async () => {
      try {
        setAttachmentsLoading(true)
        const data = await listOrderAttachments(order.id)
        if (!cancelled) {
          setAttachments(data.attachments ?? [])
        }
      } catch {
        if (!cancelled) toast.error('Anhänge konnten nicht geladen werden.')
      } finally {
        if (!cancelled) setAttachmentsLoading(false)
      }
    }
    fetchAttachments()
    return () => {
      cancelled = true
    }
  }, [isExpanded, order.id, attachmentCount])

  const uiStatus = mapApiStatusToUiStatus(order.status)

  /* ------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------ */
  return (
    <Box bg="bg.panel" borderWidth="1px" borderColor="border.default" rounded="lg" p={4}>
      {/* HEADER */}
      <HStack justify="space-between" align="center" w="full">
        <HStack color="fg" gap={6} align="center">
          <Text fontWeight="medium" fontSize="md">
            {bankName ?? 'Bank'}
          </Text>

          <HStack gap={1} align="center">
            <Box as={LuCalendarRange} color="fg.muted" />
            <Text fontSize="sm" color="fg.muted" fontWeight="normal">
              {formatPeriod(order.startTime, order.endTime)}
            </Text>
          </HStack>

          <HStack gap={1} align="center">
            <LuPaperclip size={14} />
            <Text fontSize="sm" color="fg.muted">
              {attachmentCount ?? 0}
            </Text>
          </HStack>

          <OrderStatusPill
            status={uiStatus}
            label={uiStatus === 'ABGESENDET' ? 'Offen' : undefined}
          />
        </HStack>

        <IconButton aria-label="toggle" size="sm" variant="ghost" onClick={onToggleExpand}>
          {isExpanded ? <LuChevronUp /> : <LuChevronDown />}
        </IconButton>
      </HStack>

      {isExpanded && (
        <VStack align="stretch" gap={4} mt={4}>
          {/* ORDERED SERVICES + GROSS + ATTACHMENTS */}
          <HStack align="start" justify="space-between" gap={8} wrap="wrap">
            <VStack align="start" flex="1" minW="320px" gap={2}>
              <Text fontSize="sm" color="fg.muted">
                Bestellte Leistungen
              </Text>
              <ServiceStatBoxes values={quantityUnits} hidePilotWhenZero />
            </VStack>

            <VStack align="start" minW="220px" gap={2}>
              <Box>
                <Text fontSize="sm" color="fg.muted">
                  Bruttogesamtwert Bestellung
                </Text>
                <Text fontSize="lg" fontWeight="semibold" color="fg.default">
                  {formatEUR(order.grossAmount)}
                </Text>
              </Box>

              <Box>
                <Text fontSize="sm" color="fg.muted">
                  Provision (10%)
                </Text>
                <Text fontSize="lg" fontWeight="semibold" color="fg.default">
                  {formatEUR(
                    order.grossAmount != null
                      ? calculateOrderCommission(order.grossAmount)
                      : undefined
                  )}
                </Text>
              </Box>

              <Box w="full">
                <Text fontSize="sm" color="fg.muted" mb={1}>
                  Anhänge
                </Text>
                {attachmentsLoading ? (
                  <Text fontSize="sm" color="fg.muted">
                    Anhänge werden geladen …
                  </Text>
                ) : attachments.length > 0 ? (
                  <VStack align="stretch" gap={2}>
                    {attachments.map(att => (
                      <HStack
                        key={att.id}
                        justify="space-between"
                        w="full"
                        px={3}
                        py={2}
                        borderWidth="1px"
                        borderColor="border.subtle"
                        rounded="md"
                        bg="bg"
                      >
                        <HStack gap={2} minW="0">
                          <Box as={LuFileText} color="fg.muted" flexShrink={0} />
                          <Text fontSize="sm" truncate>
                            {att.filename}
                          </Text>
                        </HStack>
                        <IconButton
                          aria-label="Download"
                          size="xs"
                          variant="ghost"
                          onClick={() =>
                            window.open(
                              `${API_BASE_URL}/orders/${order.id}/attachments/${att.id}/download`,
                              '_blank'
                            )
                          }
                        >
                          <LuDownload />
                        </IconButton>
                      </HStack>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="fg.subtle">
                    Keine Anhänge
                  </Text>
                )}
              </Box>
            </VStack>
          </HStack>

          {/* WARNING */}
          {showWarning && (
            <Box bg="yellow.subtle" rounded="l3" p={3} w="full">
              <HStack mb={1}>
                <LuInfo color="fg.muted" />
                <Text fontSize="xs" fontWeight="medium" color="yellow.fg">
                  Leichte Kapazitätsüberschreitung
                </Text>
              </HStack>
              <Text fontSize="2xs" color="yellow.fg">
                Einige Leistungen reichen über das vereinbarte Leistungskontingent des SIB hinaus.
              </Text>
            </Box>
          )}

          <Separator />

          {/* ORDER HISTORY */}
          <VStack align="stretch" gap={3}>
            <Text fontSize="sm" color="fg.muted">
              Bestellungshistorie
            </Text>
            <OrderHistoryTimeline
              orderId={order.id}
              sibName={sibName}
              bankName={bankName ?? 'Bank'}
              createdAt={order.createdAt}
            />
          </VStack>

          {/* CORRECTION */}
          {mode === 'correction' && (
            <CorrectionSection
              order={order}
              quantities={quantities}
              remainingCurrentYearUnits={remainingCurrentYearUnits}
              agreedCapacityUnits={{
                TOJ: hoursToUnits('TOJ', capacityInfo.TOJ),
                CONSULTING: hoursToUnits('CONSULTING', capacityInfo.CONSULTING),
                TRAINING: hoursToUnits('TRAINING', capacityInfo.TRAINING),
                PILOT: hoursToUnits('PILOT', capacityInfo.PILOT ?? 0),
              }}
              onClose={() => setMode('default')}
              onSuccess={() => {
                onStatusChange('CORRECTED')
                setMode('default')
                onRefresh()
              }}
            />
          )}

          {/* REJECTION */}
          {mode === 'reject' && (
            <RejectionSection
              order={order}
              onClose={() => setMode('default')}
              onSuccess={() => {
                onStatusChange('REJECTED')
                setMode('default')
                onRefresh()
              }}
            />
          )}
        </VStack>
      )}
    </Box>
  )
}

/* ------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------ */

function formatPeriod(startIso: string, endIso: string) {
  const fmt = (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '—' : new Intl.DateTimeFormat('de-DE').format(d)
  }
  return `${fmt(startIso)} – ${fmt(endIso)}`
}
